import { useEffect, useRef, useState } from 'react';
import { ChevronIcon } from './Icons.js';

// --- SVG Flag Components ---
// These components render the flags as SVG icons for consistent cross-browser display.
const FlagAZ = () => (
  <svg className="flag" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" aria-hidden="true">
    <path fill="#3f9c35" d="M.1 0h640v480H.1z" />
    <path fill="#ed2939" d="M.1 0h640v320H.1z" />
    <path fill="#00b9e4" d="M.1 0h640v160H.1z" />
    <circle cx="304" cy="240" r="72" fill="#fff" />
    <circle cx="320" cy="240" r="60" fill="#ed2939" />
    <path fill="#fff" d="m384 200 7.7 21.5 20.6-9.8-9.8 20.7L424 240l-21.5 7.7 9.8 20.6-20.6-9.8L384 280l-7.7-21.5-20.6 9.8 9.8-20.6L344 240l21.5-7.7-9.8-20.6 20.6 9.8z" />
  </svg>
);

const FlagGB = () => (
  <svg className="flag" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" aria-hidden="true">
    <path fill="#012169" d="M0 0h640v480H0z" />
    <path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0z" />
    <path fill="#C8102E" d="m424 281 216 159v40L369 281zm-184 20 6 35L54 480H0zM640 0v3L391 191l2-44L590 0zM0 0l239 176h-60L0 42z" />
    <path fill="#FFF" d="M241 0v480h160V0zM0 160v160h640V160z" />
    <path fill="#C8102E" d="M0 193v96h640v-96zM273 0v480h96V0z" />
  </svg>
);

const FlagRU = () => (
  <svg className="flag" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" aria-hidden="true">
    <path fill="#fff" d="M0 0h640v160H0z" />
    <path fill="#0039a6" d="M0 160h640v160H0z" />
    <path fill="#d52b1e" d="M0 320h640v160H0z" />
  </svg>
);

const FlagTR = () => (
  <svg className="flag" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" aria-hidden="true">
    <g fillRule="evenodd">
      <path fill="#e30a17" d="M0 0h640v480H0z" />
      <path fill="#fff" d="M407 247.5c0 66.2-54.6 119.9-122 119.9s-122-53.7-122-120 54.6-119.8 122-119.8 122 53.7 122 119.9" />
      <path fill="#e30a17" d="M413 247.5c0 53-43.6 95.9-97.5 95.9s-97.6-43-97.6-96 43.7-95.8 97.6-95.8 97.6 42.9 97.6 95.9z" />
      <path fill="#fff" d="m430.7 191.5-1 44.3-41.3 11.2 40.8 14.5-1 40.7 26.5-31.8 40.2 14-23.2-34.1 28.3-33.9-43.5 12-25.8-37z" />
    </g>
  </svg>
);

export const LANGUAGES = {
  AZ: { name: 'Azərbaycanca', short: 'AZ', flag: <FlagAZ /> },
  EN: { name: 'English', short: 'EN', flag: <FlagGB /> },
  RU: { name: 'Русский', short: 'RU', flag: <FlagRU /> },
  TR: { name: 'Türkçe', short: 'TR', flag: <FlagTR /> },
};

const CODES = Object.keys(LANGUAGES);

// Touch layouts get a flat grid of all four languages; desktop gets a compact dropdown.
const LanguageSwitcher = ({ language, onChange, label, variant = 'dropdown' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const optionRefs = useRef([]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen]);

  const choose = (code) => {
    onChange(code);
    setIsOpen(false);
  };

  if (variant === 'grid') {
    return (
      <div className="lang-grid" role="group" aria-label={label}>
        {CODES.map((code) => (
          <button
            key={code}
            type="button"
            className="lang-grid__option"
            aria-pressed={language === code}
            onClick={() => onChange(code)}
          >
            {LANGUAGES[code].flag}
            <span>{LANGUAGES[code].name}</span>
          </button>
        ))}
      </div>
    );
  }

  const focusOption = (index) => {
    const next = (index + CODES.length) % CODES.length;
    optionRefs.current[next]?.focus();
  };

  const onMenuKeyDown = (event) => {
    const current = optionRefs.current.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusOption(current + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusOption(current - 1);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      rootRef.current?.querySelector('.lang__trigger')?.focus();
    }
  };

  const onTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      requestAnimationFrame(() => focusOption(CODES.indexOf(language)));
    }
  };

  return (
    <div
      ref={rootRef}
      className="lang"
      data-open={isOpen}
      onPointerEnter={(event) => event.pointerType === 'mouse' && setIsOpen(true)}
      onPointerLeave={(event) => event.pointerType === 'mouse' && setIsOpen(false)}
    >
      <button
        type="button"
        className="lang__trigger"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`${label}: ${LANGUAGES[language].name}`}
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={onTriggerKeyDown}
      >
        {LANGUAGES[language].flag}
        <span className="lang__code">{LANGUAGES[language].short}</span>
        <ChevronIcon size={14} className="lang__chevron" />
      </button>
      <ul className="lang__menu" role="menu" onKeyDown={onMenuKeyDown}>
        {CODES.map((code, index) => (
          <li key={code} role="none">
            <button
              ref={(node) => (optionRefs.current[index] = node)}
              type="button"
              role="menuitemradio"
              aria-checked={language === code}
              tabIndex={isOpen ? 0 : -1}
              className="lang__option"
              onClick={() => choose(code)}
            >
              {LANGUAGES[code].flag}
              <span>{LANGUAGES[code].name}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LanguageSwitcher;
